import { HelpIcon, type HelpIconProps, type HelpIconSize } from "./HelpIcon";
import { useGuidedTour } from "./useGuidedTour";
import type { HelpStep } from "./types";

export type { HelpStep } from "./types";

export interface HelpProps
	extends Omit<HelpIconProps, "onClick" | "disabled"> {
	/** Joyride/Help steps. The icon is disabled when the array is empty. */
	guideSteps: HelpStep[];
	/** Fired once the tour completes or is dismissed. */
	onAfterShowMe?: () => void;
	/** Fired before the tour starts. Return `false` to cancel. */
	onBeforeShowMe?: () => boolean | void | Promise<boolean | void>;
}

/**
 * Help — drop-in trigger + guided tour.
 *
 * Composes `useGuidedTour` (lifecycle, step orchestration) with `HelpIcon`
 * (presentation). For custom triggers — menu items, banner buttons, etc. —
 * use `useGuidedTour` directly and render your own UI.
 */
export const Help: React.FC<HelpProps> = ({
	guideSteps,
	onBeforeShowMe,
	onAfterShowMe,
	...iconProps
}) => {
	const { start, TourPortal } = useGuidedTour({
		steps: guideSteps,
		onBeforeStart: onBeforeShowMe,
		onAfterEnd: onAfterShowMe,
	});

	return (
		<>
			<HelpIcon
				{...iconProps}
				onClick={() => void start()}
				disabled={guideSteps.length === 0}
			/>
			{TourPortal}
		</>
	);
};

export type { HelpIconProps, HelpIconSize };
